import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useMembersList } from '@/hooks/use-members'
import { getApiErrorMessage } from '@/lib/api'
import { formatKES } from '@/lib/auth'

interface AssignPlotOwnerModalProps {
  open: boolean
  onClose: () => void
  plotLabel: string
  currentOwnerName?: string | null
  /** When > 0, an override reason is required before this plot can change hands (admin-only gate). */
  outstandingBalance?: number
  onAssign: (memberId: string, transferNote?: string, overrideDuesReason?: string) => Promise<void>
  loading?: boolean
}

export function AssignPlotOwnerModal({
  open,
  onClose,
  plotLabel,
  currentOwnerName,
  outstandingBalance = 0,
  onAssign,
  loading,
}: AssignPlotOwnerModalProps) {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [memberId, setMemberId] = useState('')
  const [transferNote, setTransferNote] = useState('')
  const [overrideDuesReason, setOverrideDuesReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    if (!open) {
      setSearch('')
      setMemberId('')
      setTransferNote('')
      setOverrideDuesReason('')
      setError(null)
    }
  }, [open])

  const { data } = useMembersList({
    page: 1,
    limit: 20,
    search: debouncedSearch || undefined,
    status: 'active',
    role: 'member',
  })

  const members = (data?.data ?? []).filter((m) => m.role !== 'admin')

  if (!open) return null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!memberId) {
      setError('Select a member')
      return
    }
    if (outstandingBalance > 0 && !overrideDuesReason.trim()) {
      setError('An override reason is required — this plot has an outstanding balance')
      return
    }
    setError(null)
    try {
      await onAssign(
        memberId,
        transferNote.trim() || undefined,
        outstandingBalance > 0 ? overrideDuesReason.trim() : undefined,
      )
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not assign owner'))
    }
  }

  return (
    <div className="fixed inset-0 z-[60]">
      <button
        type="button"
        className="fixed inset-0 bg-navy/40"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div className="fixed inset-0 overflow-y-auto overscroll-contain">
        <div className="flex min-h-full items-end justify-center p-4 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] sm:items-center sm:pb-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="assign-plot-title"
            className="relative w-full max-w-md rounded-card border border-border-2 bg-card p-6 shadow-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 text-muted-foreground hover:text-ink"
              aria-label="Close"
            >
              <X className="size-5" />
            </button>

            <h2 id="assign-plot-title" className="font-serif text-xl text-ink">
              {currentOwnerName ? 'Transfer ownership' : 'Assign owner'}
            </h2>
            <p className="mt-1 font-sans text-sm text-muted-foreground">
              Plot {plotLabel}
              {currentOwnerName && (
                <>
                  {' '}
                  — current owner: <strong className="text-ink">{currentOwnerName}</strong>
                </>
              )}
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <Input
                label="Search members"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, phone, email…"
              />

              <div className="max-h-48 overflow-y-auto rounded border border-border-2">
                {members.length === 0 ? (
                  <p className="px-3 py-4 font-sans text-sm text-muted-foreground">
                    No members found
                  </p>
                ) : (
                  members.map((m) => (
                    <label
                      key={m.id}
                      className="flex cursor-pointer items-center gap-3 border-b border-border-2 px-3 py-2 last:border-0 hover:bg-cream-2/50"
                    >
                      <input
                        type="radio"
                        name="member"
                        checked={memberId === m.id}
                        onChange={() => setMemberId(m.id)}
                      />
                      <span className="font-sans text-sm text-ink">{m.name}</span>
                    </label>
                  ))
                )}
              </div>

              <Input
                label="Transfer note (optional)"
                value={transferNote}
                onChange={(e) => setTransferNote(e.target.value)}
                placeholder="Reason for transfer"
              />

              {outstandingBalance > 0 && (
                <div className="space-y-3 rounded border border-destructive/30 bg-destructive/5 px-3 py-2.5">
                  <p className="font-sans text-sm text-destructive">
                    This plot has an outstanding balance of{' '}
                    <strong>{formatKES(outstandingBalance)}</strong>. Transferring it requires an
                    override reason.
                  </p>
                  <Input
                    label="Override reason"
                    value={overrideDuesReason}
                    onChange={(e) => setOverrideDuesReason(e.target.value)}
                    placeholder="e.g. foreclosure, correction, hardship waiver"
                    required
                  />
                </div>
              )}

              {error && (
                <p className="font-sans text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" loading={loading}>
                  {currentOwnerName ? 'Transfer' : 'Assign'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
